import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { st, classes } from './CheckToggle.st.css';
import { dataHooks, SIZE, SKIN } from './constants';
import Tooltip from '../Tooltip';
import { TooltipCommonProps } from '../common/PropTypes/TooltipCommon';
import { InjectedFocusableProps, withFocusable } from '../common/Focusable';
import { ConfirmSmall, Confirm } from '@wix/wix-ui-icons-common';
import { WixStyleReactContext } from '../WixStyleReactProvider/context';
import { CheckToggleProps } from '.';

const icon = {
  small: <ConfirmSmall />,
  medium: <Confirm />,
};

/** CheckToggle */
export class CheckToggle extends PureComponent<
  InjectedFocusableProps<CheckToggleProps, HTMLLabelElement>
> {
  static displayName = 'CheckToggle';

  static propTypes = {
    dataHook: PropTypes.string,
    className: PropTypes.string,
    checked: PropTypes.bool,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(Object.values(SIZE)),
    skin: PropTypes.oneOf(Object.values(SKIN)),
    tooltipContent: PropTypes.node,
    tooltipProps: PropTypes.shape(TooltipCommonProps),
    focusableOnFocus: PropTypes.func,
    focusableOnBlur: PropTypes.func,
  };

  static defaultProps: CheckToggleProps = {
    disabled: false,
    size: 'small',
    skin: 'standard',
  };

  state = {
    checked: !!this.props.checked,
  };

  /**
   * Checks if the component is controlled or uncontrolled.
   * The component is controlled only if prop checked is provided.
   *
   * @returns boolean
   * @private
   */
  _isControlled = () => {
    return this.props.hasOwnProperty('checked');
  };

  /**
   * Toggles checked state and triggers the onChange callback.
   * Except when disabled
   */
  _handleChange = (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = this.state;
    const { onChange } = this.props;

    this.setState({ checked: !checked }, () => {
      if (onChange) onChange(changeEvent);
    });
  };

  /**
   * Renders the toggle itself
   * @returns React.ReactNode
   * @private
   */
  _renderInput = () => {
    const { checked } = this._isControlled() ? this.props : this.state;
    const { size, disabled, onChange } = this.props;

    if (!size) return null;

    return (
      <>
        <input
          type="checkbox"
          className={classes.input}
          data-hook={dataHooks.toggle}
          checked={checked}
          disabled={disabled}
          onChange={this._isControlled() ? onChange : this._handleChange}
        />
        <span className={classes.toggle}>{icon[size]}</span>
      </>
    );
  };

  /**
   * Renders a tooltip wrapper
   * @returns React.ReactNode
   * @private
   */
  _renderTooltip = () => {
    const { tooltipContent, tooltipProps } = this.props;
    return (
      <Tooltip
        dataHook={dataHooks.tooltip}
        content={tooltipContent}
        {...tooltipProps}
      >
        {this._renderInput()}
      </Tooltip>
    );
  };

  render() {
    const { checked } = this._isControlled() ? this.props : this.state;
    const {
      dataHook,
      size,
      skin,
      disabled,
      tooltipContent,
      focusableOnFocus,
      focusableOnBlur,
      className,
    } = this.props;

    return (
      <WixStyleReactContext.Consumer>
        {({ newColorsBranding }) => (
          <label
            className={st(
              classes.root,
              { checked, size, skin, disabled, newColorsBranding },
              className,
            )}
            data-hook={dataHook}
            onFocus={focusableOnFocus}
            onBlur={focusableOnBlur}
          >
            {tooltipContent ? this._renderTooltip() : this._renderInput()}
          </label>
        )}
      </WixStyleReactContext.Consumer>
    );
  }
}
// @ts-ignore
export default withFocusable<CheckToggleProps, HTMLLabelElement>(CheckToggle);
